import {
  cargoSubtype,
  FreightTypeEntity,
  getObjectId,
  isBefore,
  TransportCategory,
  transportCategory
} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Group, Modal, ModalProps, Switch} from "@mantine/core";
import {useCallback} from "react";
import {DateInput} from "../../date/DateInput/DateInput";
import {EntityTypeSelect} from "../../select/entity/EntityTypeSelect/EntityTypeSelect";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";
import {EntityMultiSelect} from "../../select/entity/EntityMultiSelect/EntityMultiSelect";
import {DefaultTFuncReturn} from "i18next";
import { InputInfoTooltip } from "../../tooltip/InfoTooltip/InfoTooltip";

interface PipelineFlowSearchFormProps {
  options?: PipelineFlowSearchFormOptions;
  defaultValues?: Partial<PipelineFlowSearchFormValues>;
  onSubmit?: (values: PipelineFlowSearchFormValues) => void;
  submitting?: boolean;
}

export interface PipelineFlowSearchFormOptions {
  regionId?: string
  category?: TransportCategory
}

export interface PipelineFlowSearchFormValues {
  fromDate: Date
  toDate?: Date | undefined
  entityType?: string | undefined
  entityIds?: string[] | undefined
  freightType?: FreightTypeEntity | string | undefined;
  meterGenerated?: boolean
  ignoreMeterGenerated?: boolean
}

const getDefaultValues = (defaultValues: Partial<PipelineFlowSearchFormValues> | undefined) => {
  return {
    fromDate: defaultValues?.fromDate,
    toDate: defaultValues?.toDate,
    entityType: defaultValues?.entityType,
    entityIds: defaultValues?.entityIds,
    freightType: getObjectId(defaultValues?.freightType),
    meterGenerated: defaultValues?.meterGenerated ?? true,
    ignoreMeterGenerated: defaultValues?.ignoreMeterGenerated ?? true,
  }
}

export const PipelineFlowSearchForm = (props: PipelineFlowSearchFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {
      toDate: (value, values) => {
        let error: string | undefined | DefaultTFuncReturn = undefined
        if (value && values.fromDate && isBefore(value, values.fromDate)) {
          error = t<string>("common.form.validation.todatebeforefrom", "The 'to' date can't be before the 'from' date")
        }
        return error
      }
    }
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        fromDate: values.fromDate!,
        toDate: values.toDate,
        entityType: values.entityType,
        entityIds: values.entityIds,
        freightType: values.freightType,
        meterGenerated: values.meterGenerated ? undefined : values.meterGenerated,
        ignoreMeterGenerated: values.ignoreMeterGenerated,
      })
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Group align="flex-end">
        <Grid>
          <Grid.Col span={6}>
            <DateInput
              valueFormat="MM/DD/YYYY HH:mm"
              label={t("common.entity.pipelineflow.search.form.field.fromdate", "From Date")}
              placeholder={t<string>("common.entity.pipelineflow.search.form.field.fromdate", "From Date")}
              clearable
              withAsterisk
              popoverProps={{
                withinPortal: true
              }}
              styles={{wrapper: {width: "200px"}}}
              required
              {...form.getInputProps("fromDate")} />
          </Grid.Col>
          <Grid.Col span={6}>
            <DateInput
              miw={100}
              valueFormat="MM/DD/YYYY HH:mm"
              label={t("common.entity.pipelineflow.search.form.field.todate", "To Date")}
              placeholder={t<string>("common.entity.pipelineflow.search.form.field.todate", "To Date")}
              clearable
              popoverProps={{
                withinPortal: true
              }}
              styles={{wrapper: {width: "200px"}}}
              {...form.getInputProps("toDate")} />
          </Grid.Col>
        </Grid>
      </Group>
      <Divider my="sm" variant="dotted"/>
      <EntityTypeSelect
        options={['SITE', 'WELL']}
        label={t("common.entity.pipelineflow.search.form.field.object", "Object")}
        placeholder={t<string>("common.entity.pipelineflow.search.form.field.object.placeholder", "Object")}
        withinPortal={true}
        clearable
        {...form.getInputProps("entityType")} />
      <EntityMultiSelect
        entityType={form.getTransformedValues()?.entityType}
        regionId={options?.regionId}
        pt="md"
        dropdownPosition="bottom"
        limit={20}
        maxSelectedValues={5}
        withinPortal={true}
        clearable
        {...form.getInputProps("entityIds")} />
      <Divider my="sm" variant="dotted"/>
      <FreightTypeSelect
        label={t("common.entity.pipelineflow.search.form.field.freighttype", "Freight Type")}
        placeholder={t<string>("common.entity.pipelineflow.search.form.field.object.freighttype", "Freight Type")}
        selector={{
          regionId: options?.regionId,
          cargoSubtype: options?.category === transportCategory.oil ? cargoSubtype.oil : undefined
        }}
        clearable
        withinPortal={true}
        {...form.getInputProps("freightType")} />
      <Divider my="sm" variant="dotted"/>
      <Switch
        mb="sm"
        label={
          <Group spacing={"xs"}>
            {t("common.entity.pipelineflow.search.form.field.metergenerated", "Include Meter Flow Volumes")}
            <InputInfoTooltip
              label={t("common.entity.pipelineflow.search.form.field.generatemeterflows.tip", "Whether or not to include flow volumes from generated from meter flow readings.")}/>
          </Group>
        }
        {...form.getInputProps("meterGenerated", {type: 'checkbox'})} />
      <Switch
        mb="sm"
        label={
          <Group spacing={"xs"}>
            {t("common.entity.pipelineflow.search.form.field.ignoremetergenerated", "Ignore Meter Flow Volumes")}
            <InputInfoTooltip
              label={t("common.entity.pipelineflow.search.form.field.prioritzepipelineflows.tip", "Whether or not to show meter flow volumes if a flow volume already exists for the time, object, and freight type.")}/>
          </Group>
        }
        {...form.getInputProps("ignoreMeterGenerated", {type: 'checkbox'})} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface PipelineFlowSearchFormModalProps extends PipelineFlowSearchFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const PipelineFlowSearchFormModal = (props: PipelineFlowSearchFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <PipelineFlowSearchForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        submitting={submitting}/>
    </Modal>
  );
};
